import { useObserver } from 'mobx-react';
import appStore from '../stores/appStore';

/**
 * Node in the graph of businesses. Contains any child businesses that belong to this business.
 */
class BusinessGraphNode {
  /**
   * @param {Object} business - The business object.
   * @param {number} business.Id - The business ID.
   * @param {number} [business.RootBusinessId] - The parent business ID.
   * @param {string} business.Name - The business name.
   * @param {BusinessGraphNode[]} children - The child business nodes.
   */
  constructor(business, children) {
    this.business = business;
    this.businessId = business.Id;
    this.children = children;
    this.parentBusinessId = business.RootBusinessId || undefined;
    this.name = business.Name;
    this.isRoot = !business.RootBusinessId;
  }

  /**
   * Builds a business graph for a given business.
   * @param {Object} forBusiness - The business object.
   * @param {Object[]} businesses - The list of all businesses.
   * @returns {BusinessGraphNode} The root node of the business graph.
   */
  static buildBusinessGraph(forBusiness, businesses) {
    const directChildren = businesses.filter(
      (b) => b.RootBusinessId === forBusiness.Id
    );

    return new BusinessGraphNode(
      forBusiness,
      directChildren.map((c) =>
        BusinessGraphNode.buildBusinessGraph(c, businesses)
      )
    );
  }

  /**
   * Builds business graphs for all root businesses.
   * @param {Object[]} businesses - The list of all businesses.
   * @returns {BusinessGraphNode[]} The list of root business nodes.
   */
  static buildBusinessGraphs(businesses) {
    const roots = businesses.filter((b) => !b.RootBusinessId);

    return roots.map((root) =>
      BusinessGraphNode.buildBusinessGraph(root, businesses)
    );
  }

  /**
   * Creates a new BusinessGraphNode with updated children.
   * @param {BusinessGraphNode[]} children - The new child nodes.
   * @returns {BusinessGraphNode} The updated business graph node.
   */
  withChildren(children) {
    return new BusinessGraphNode(this.business, children);
  }
}

/**
 * Filters out nodes that do not have resources.
 * @param {Set<number>} businessesWithResources - The set of business IDs with resources.
 * @param {BusinessGraphNode[]} rootNodes - The list of root business nodes.
 * @returns {BusinessGraphNode[]} The filtered list of root business nodes.
 */
function filterNodes(businessesWithResources, rootNodes) {
  function filterNode(node) {
    if (node.children.length > 0) {
      return node.withChildren(
        node.children.map(filterNode).filter((n) => n !== null)
      );
    } else if (businessesWithResources.has(node.businessId)) {
      return node;
    } else {
      return null;
    }
  }

  return rootNodes.map(filterNode).filter((n) => n !== null);
}

/**
 * Gets the constructed graph of businesses available in the MobX store.
 * @param {Set<number>} businessesWithResources - The set of business IDs with resources.
 * @returns {BusinessGraphNode[]} The list of root business nodes.
 */
export const useRootBusinesses = (businessesWithResources, rootBusinesses) => {
  const rootNodes = BusinessGraphNode.buildBusinessGraphs(rootBusinesses);
  return filterNodes(businessesWithResources, rootNodes);
};
